/**
 * Everything currently narrowing this audience, on one line, each removable.
 *
 * This is the answer to the only question the form itself cannot answer at a
 * glance: what is actually switched on. Twelve controls that all read "any" look
 * identical to twelve controls where two do not, and half of this form now lives
 * behind an "advanced" fold - so without a summary a filter can be applied and
 * invisible at the same time. Removing one is a click here rather than a hunt
 * through the fold.
 *
 * The chip list itself is built in lib/audienceChips.ts, shared with the
 * register list page so the two screens cannot word the same filter differently.
 */
import { X } from "lucide-react";
import { Button } from "@wa/ui";
import { useT } from "../../../../../i18n/I18nProvider";
import type { AudienceChip } from "../../../../../lib/audienceChips";

export function FilterChips({
  chips,
  onRemove,
  onClearAll,
  disabled,
}: {
  chips: AudienceChip[];
  onRemove: (chip: AudienceChip) => void;
  onClearAll: () => void;
  disabled?: boolean;
}) {
  const t = useT();

  if (chips.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-border bg-muted/20 px-3.5 py-2.5 text-xs text-muted-foreground">
        {t("marketing.audience.noFilters")}
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
        {t("marketing.audience.active")}
      </span>
      {chips.map((c) => (
        <span
          key={c.key}
          className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 py-1 pl-2.5 pr-1 text-xs"
        >
          <span className="max-w-[18rem] truncate">{c.label}</span>
          {!disabled && (
            <button
              type="button"
              onClick={() => onRemove(c)}
              aria-label={t("common.clear")}
              className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive"
            >
              <X className="size-3" />
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <Button variant="ghost" size="sm" onClick={onClearAll} className="h-6 px-2 text-[11px]">
          {t("marketing.audience.clear")}
        </Button>
      )}
    </div>
  );
}
